/**
 * **New client** — `docs/rowted-admin-v3.md` §2.1, board 1.
 *
 * The standard `Sheet` (`docs/record-flyouts.md`), two sections, one check
 * row. What it posts goes to `POST /platform/clients`, which calls `signUp()`
 * — the same function the public sign-up page calls — so the customer that
 * comes out is the same customer either way. This form's whole job is the four
 * things an admin gets to decide on top: the short name, the kind, the trial
 * length, and whether the owner is invited now.
 *
 * **The short name is checked as it is typed** (`GET /clients/slug-available`),
 * debounced, and the two failure modes are told apart: "that is not a short
 * name" and "somebody has that one". A server that only said no at save time
 * would make somebody fill the whole form to find out.
 *
 * **No City and State**, which §2.1 asks for. `platform.tenants` has no
 * address, and `company_profile.city`/`state` is the *customer's* own record of
 * themselves — what prints on their invoices. Typing a guess into it from
 * here would put Rowted's typing on somebody else's paperwork, and the
 * customer fills it in during set-up anyway.
 *
 * **No "Start with the sample data"**, also in §2.1: `sample_catalog_offer`
 * is a flag signup already sets to `pending`, and nothing in the product
 * applies a sample catalogue yet. A tick box that does nothing is worse than
 * an absent one.
 */
import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import {
  ApiError, RESERVED_SLUGS, SLUG_MAX, SLUG_MIN, slugify,
} from '@rowted/shared'
import { adminRequest } from '../../lib/admin-api.js'
import { useHost } from '../../lib/use-host.js'
import type { StepUp } from '../../pages/admin/step-up.js'
import { Button, Segmented, Switch } from '../primitives.js'
import { Field, FieldShell, SelectField } from '../fields.js'
import { cn } from '../../lib/cn.js'
import { Sheet } from '../sheet.js'
import { TIMEZONES } from '../../lib/timezones.js'
import { IconCheck, IconSpinner, IconWarn } from '../../frame/icons.js'

export interface Tier {
  key: string
  name: string
  baseCents: number
  seatCents: number
  includedSeats: number
}

export interface NewClientResult {
  tenantId: string
  slug: string | null
  ownerUserId: string
  setPasswordUrl: string
  invited: boolean
}

type Kind = 'production' | 'trial' | 'demo'

/** One line under the segment, so the choice explains itself where it is made. */
const KIND_NOTE: Record<Kind, string> = {
  production: 'No trial. The subscription opens unpaid until a card is added.',
  trial: 'A trial that ends on its own, with the nudge emails.',
  demo: 'For showing the product. Not billed.',
}

/** `$349 + $12 a seat over 5` — what a plan costs, in one line. */
const priceLine = (tier: Tier): string => {
  const money = (cents: number) => `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0, maximumFractionDigits: 2,
  })}`
  return `${money(tier.baseCents)} a month`
    + (tier.seatCents > 0
      ? ` + ${money(tier.seatCents)} a seat over ${tier.includedSeats}`
      : ` · ${tier.includedSeats} seats`)
}

/*
 * `slugify` and the shape rule come from `shared/src/tenant-slug.ts` now —
 * `docs/identity-and-sign-in.md` §5.5. There used to be a copy here, a copy in
 * `freeSlug`, a copy in `claimSlug` and a fourth in `/clients/slug-available`,
 * all "the same rule" and one of them forty characters long. A short name is a
 * **host label** now, so they cannot be allowed to differ: what the field says
 * is free has to be what the insert accepts.
 */

type SlugState =
  | { kind: 'idle' }
  | { kind: 'checking' }
  /** `host` is what the field prints — `keystone.app.rowted.com`, from the server. */
  | { kind: 'free'; host: string }
  | { kind: 'taken' }
  /** The server's own sentence, so the field and a 400 cannot disagree. */
  | { kind: 'malformed'; problem: string }

export function NewClientFlyout({ tiers, trialDays, guard, onClose, onMade }: {
  tiers: Tier[]
  /** `settings.trials.days`, so the field opens on what a signup would get. */
  trialDays: number
  /**
   * `useStepUp().guard` from the page, which also renders the dialog.
   *
   * **Creating a client is a step-up** — `POST /platform/clients` is behind
   * `requirePlatformStepUp`, because it opens an account that gets billed —
   * and this form used to call `adminRequest` straight. So a lapsed window
   * answered `step_up_required`, the sentence landed in the error banner, and
   * there was nowhere to type a code: the footer note said "Creating asks for
   * your sign-in code again" above a form that could not ask. `guard` replays
   * the whole submit after the code, which is why the answer is read inside
   * it rather than from what it resolves.
   */
  guard: StepUp['guard']
  onClose: () => void
  /** `signIn` is the *Create and sign in as owner* path. */
  onMade: (made: NewClientResult, signIn: boolean) => void
}) {
  const [company, setCompany] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [kind, setKind] = useState<Kind>('trial')
  const [tier, setTier] = useState(tiers[0]?.key ?? '')
  const [days, setDays] = useState(String(trialDays))
  const [timezone, setTimezone] = useState('America/New_York')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')
  const [invite, setInvite] = useState(true)
  const [busy, setBusy] = useState<null | 'create' | 'signIn'>(null)
  const [error, setError] = useState<string | null>(null)
  const [slugState, setSlugState] = useState<SlugState>({ kind: 'idle' })
  const slugFieldId = useId()
  /*
   * The suffix beside the box, from the server — `APP_ROOT_HOST`, which is
   * where a *tenant* lives.
   *
   * It used to be the browser's own address minus its first label, on the
   * reasoning that the console sits on the front door. It does not: Rowted
   * Admin has a host of its own, so stripping one label off `admin.rowted.com`
   * printed `.rowted.com` and the field promised an address no client will
   * ever have. `GET /host` answers the real one (`identity-and-sign-in.md`
   * §5), and the console's host has nothing to say about where a customer's
   * app is served.
   */
  const host = useHost()
  const rootHost = host.state === 'ready' ? host.host.rootHost
    : host.state === 'nobody' ? host.rootHost
      : null

  /*
   * The short name follows the company until somebody types in it. After that
   * it is theirs — a field that kept rewriting itself under the cursor would
   * be a field nobody could correct.
   */
  const effectiveSlug = slugTouched ? slug : slugify(company)

  useEffect(() => {
    if (effectiveSlug === '') { setSlugState({ kind: 'idle' }); return undefined }
    setSlugState({ kind: 'checking' })
    const timer = setTimeout(() => {
      void adminRequest<{
        available: boolean; wellFormed: boolean; host?: string; problem?: string
      }>(`/clients/slug-available?slug=${encodeURIComponent(effectiveSlug)}`)
        .then((answer) => setSlugState(
          !answer.wellFormed
            ? {
              kind: 'malformed',
              problem: answer.problem
                ?? 'Letters, numbers and dashes, 2 to 30 characters.',
            }
            : answer.available
              ? { kind: 'free', host: answer.host ?? effectiveSlug }
              : { kind: 'taken' }))
        .catch(() => setSlugState({ kind: 'idle' }))
    }, 350)
    return () => clearTimeout(timer)
  }, [effectiveSlug])

  /*
   * The first plan, once the list arrives. The field is built before the
   * settings read finishes, so a `useState(tiers[0]?.key)` was `''` forever
   * and the select drew blank with no error to show for it.
   */
  useEffect(() => {
    if (tier === '' && tiers[0]) setTier(tiers[0].key)
  }, [tier, tiers])

  const chosen = useMemo(
    () => tiers.find((one) => one.key === tier) ?? null, [tiers, tier])

  const trialEnds = useMemo(() => {
    const n = Number(days)
    if (!Number.isFinite(n) || n <= 0) return null
    return new Date(Date.now() + n * 86_400_000)
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }, [days])

  const ready = company.trim() !== ''
    && firstName.trim() !== '' && lastName.trim() !== ''
    && /.+@.+\..+/.test(email.trim())
    && tier !== ''
    && slugState.kind !== 'taken' && slugState.kind !== 'malformed'

  const submit = useCallback(async (signIn: boolean) => {
    setBusy(signIn ? 'signIn' : 'create')
    setError(null)
    try {
      await guard(async () => {
      const made = await adminRequest<NewClientResult>('/clients', {
        method: 'POST',
        body: {
          company: company.trim(),
          ...(effectiveSlug ? { slug: effectiveSlug } : {}),
          kind,
          tier,
          ...(kind === 'production' ? {} : { trialDays: Number(days) || trialDays }),
          timezone,
          owner: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            ...(mobile.trim() ? { mobile: mobile.trim() } : {}),
          },
          sendInvite: invite,
        },
      })
      onMade(made, signIn)
      })
      /* `guard` resolves false when the code dialog was closed. The form
         stays as it was, with everything still typed in it. */
      setBusy(null)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not go through.')
      setBusy(null)
    }
  }, [company, effectiveSlug, kind, tier, days, trialDays, timezone,
      firstName, lastName, email, mobile, invite, guard, onMade])

  return (
    <Sheet
      open
      onClose={onClose}
      kind="company"
      title="New client"
      sub="Clients · the same account a signup makes"
      width={620}
      dirty={company.trim() !== '' || email.trim() !== ''}
      saveLabel="Create client"
      onSave={() => void submit(false)}
      note={(
        <span className="text-[11.5px] leading-[1.45] text-text-faint">
          Creating asks for your sign-in code again.
        </span>
      )}
      secondary={(
        <Button
          disabled={!ready || busy !== null}
          onClick={() => void submit(true)}
        >
          {busy === 'signIn' ? <IconSpinner size={15} /> : null}
          Create and sign in as owner
        </Button>
      )}
      primary={(
        <Button variant="primary" disabled={!ready || busy !== null} onClick={() => void submit(false)}>
          {busy === 'create' ? <IconSpinner size={15} /> : null}
          Create client
        </Button>
      )}
    >
      <div className="flex flex-col gap-5 p-4">
        {error && (
          <p role="alert" className="m-0 rounded-field bg-danger-bg px-3 py-2 text-cell
                                     text-danger">
            {error}
          </p>
        )}

        <Section title="Company">
          <Field label="Company name" value={company} onChange={setCompany} required autoFocus />

          {/*
            * Board 5 draws this one itself, because `Field` has no suffix slot
            * and the suffix is the whole point: the short name **is** the
            * address, so `.app.rowted.com` belongs inside the box beside what
            * is being typed rather than in a hint underneath saying "what
            * comes after app.rowted.com/", which described a path that stops
            * existing after the legacy redirect.
            */}
          <div className="flex flex-col gap-1">
            <FieldShell
              label="Short name"
              required
              htmlFor={slugFieldId}
              hint={`Letters, numbers and hyphens · ${SLUG_MIN}–${SLUG_MAX} · this becomes `
                + `the sign-in address · reserved: ${RESERVED_SLUGS.join(', ')}`}
            >
              <span className={cn('flex h-9 w-full items-center rounded-field border',
                'border-line bg-surface px-2')}
              >
                <input
                  id={slugFieldId}
                  value={effectiveSlug}
                  onChange={(event) => { setSlugTouched(true); setSlug(event.target.value) }}
                  autoCapitalize="none"
                  spellCheck={false}
                  className={cn('min-w-0 flex-1 border-0 bg-transparent p-0 text-cell',
                    'font-semibold text-text outline-none placeholder:text-text-faint')}
                />
                {/* Nothing until the server has answered: a suffix that
                    changes under the cursor is worse than one that arrives. */}
                {rootHost && (
                  <span className="shrink-0 pl-2 text-[12px] text-text-faint">
                    .{rootHost}
                  </span>
                )}
              </span>
            </FieldShell>
            <SlugNote state={slugState} slug={effectiveSlug} />
          </div>

          <SelectField label="Time zone" value={timezone} onChange={setTimezone}>
            {TIMEZONES.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
          </SelectField>

          <div className="flex flex-col gap-1.5">
            <Segmented
              label="What sort of client"
              value={kind}
              onChange={(next) => setKind(next)}
              size="field"
              options={[
                { value: 'production', label: 'Production' },
                { value: 'trial', label: 'Trial' },
                { value: 'demo', label: 'Demo' },
              ]}
            />
            <p className="m-0 text-[11.5px] leading-[1.45] text-text-muted">
              {KIND_NOTE[kind]}
            </p>
          </div>

          <SelectField
            label="Plan"
            value={tier}
            onChange={setTier}
            {...(chosen ? { hint: priceLine(chosen) } : {})}
          >
            {tiers.map((one) => (
              <option key={one.key} value={one.key}>{one.name}</option>
            ))}
          </SelectField>

          {/*
            * Hidden for a production client, because they have no trial at
            * all — a "trial length" on an account that skips the trial is a
            * number that means nothing.
            */}
          {kind !== 'production' && (
            <Field
              label="Trial length"
              value={days}
              onChange={setDays}
              type="number"
              {...(trialEnds ? { hint: `Ends ${trialEnds}` } : {})}
            />
          )}
        </Section>

        <Section title="Owner">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="First name" value={firstName} onChange={setFirstName} required />
            <Field label="Last name" value={lastName} onChange={setLastName} required />
          </div>
          <Field label="Email" value={email} onChange={setEmail} type="email" required />
          <Field
            label="Mobile"
            value={mobile}
            onChange={setMobile}
            hint="Optional. Only for reaching them about the account."
          />
          <Switch
            checked={invite}
            onChange={setInvite}
            label="Send the owner an invite now"
          />
        </Section>

        {/*
          * What Create actually does, in the customer's own words, before
          * anybody presses it. A trial that starts a clock and an email that
          * goes to a real person are both hard to take back.
          */}
        <p className="m-0 rounded-field border border-accent-line bg-accent-soft px-3 py-2.5
                      text-cell leading-[1.5] text-accent-dark">
          Creates <b>{company.trim() || 'the company'}</b> on
          {' '}{chosen?.name ?? 'a plan'}
          {kind === 'production'
            ? ', with no trial and the subscription unpaid until a card is added'
            : `, with a ${Number(days) || trialDays}-day trial`}
          , and makes{' '}
          <b>{[firstName, lastName].map((part) => part.trim()).filter(Boolean).join(' ')
            || 'the owner'}</b> its Owner
          {invite ? ' and emails them a link to set a password.' : '. No email is sent.'}
        </p>
      </div>
    </Sheet>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="m-0 text-[10.5px] font-semibold uppercase tracking-[0.055em] text-text-faint">
        {title}
      </h3>
      {children}
    </section>
  )
}

/**
 * Free, taken, or not a short name at all — three answers, not two.
 *
 * It prints the **address**, not a path. The short name is the company's
 * subdomain now (`docs/identity-and-sign-in.md` §5.5), so a note reading
 * `app.rowted.com/keystone` would be describing a URL that redirects for one
 * release and then stops existing.
 */
function SlugNote({ state, slug }: { state: SlugState; slug: string }) {
  if (slug === '') {
    return (
      <p className="m-0 text-[11.5px] text-text-faint">
        Taken from the company name unless you type one.
      </p>
    )
  }
  if (state.kind === 'checking' || state.kind === 'idle') {
    /* Nothing yet — the box already shows the address, so this stays quiet. */
    return <p className="m-0 text-[11.5px] text-text-faint">Checking…</p>
  }
  if (state.kind === 'free') {
    return (
      <p className="m-0 flex items-center gap-1 text-[12.5px] text-accent-dark">
        <IconCheck size={14} />
        {state.host} is available
      </p>
    )
  }
  return (
    <p className="m-0 flex items-center gap-1 text-[11.5px] text-danger">
      <IconWarn size={13} />
      {state.kind === 'taken'
        ? 'Somebody already has that short name.'
        : state.problem}
    </p>
  )
}
